import React from "react";
import PropTypes from "prop-types";
import {
  Button,
  TextField,
  Box,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import MDTypography from "components/MDTypography";
import { addTrunkRequest, updateTrunkRequest, getOutboundCdrRequest } from "api/api";

// Validation
const validationSchema = Yup.object({
  name: Yup.string().required("Trunk name is required"),
  ip: Yup.string().required("IP address is required"),
  prefix: Yup.string(), // Optional
  remove_prefix: Yup.string(), // Optional
});

function AddTrunkForm({ onSuccess, initialData = null }) {
  const [failoverOptions, setFailoverOptions] = React.useState([]);
  React.useEffect(() => {
    const fetchFailoverOptions = async () => {
      try {
        const res = await getOutboundCdrRequest();
        setFailoverOptions(res.data.data); // Make sure your API returns `data.data`
      } catch (error) {
        console.error("Error fetching failover options:", error);
      }
    };

    fetchFailoverOptions();
  }, []);

  const isEditMode = Boolean(initialData);

  const formik = useFormik({
    initialValues: {
      name: initialData?.name || "",
      ip: initialData?.ip || "",
      port: initialData?.port || "5060",
      prefix: initialData?.prefix || "",
      remove_prefix: initialData?.remove_prefix || "",
      username: initialData?.username || "",
      password: initialData?.password || "",
      failover: initialData?.failover || "",
      priority: initialData?.priority || "",
    },
    enableReinitialize: true, // ⚠️ important to re-fill when props change
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        let response;

        if (isEditMode) {
          response = await updateTrunkRequest({ ...values, id: initialData.id });
        } else {
          response = await addTrunkRequest(values);
        }

        console.log("Trunk saved:", response.data);
        resetForm();
        if (onSuccess) onSuccess();
      } catch (error) {
        console.error("Trunk operation failed:", error);
      }
    },
  });

  return (
    <Box sx={{ p: 3, maxWidth: 600, mx: "auto" }}>
      {/* <MDTypography variant="h5" mb={2}>
        Add New Trunk
      </MDTypography> */}

      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={2}>
          {/* Trunk Name */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              id="name"
              name="name"
              label="Trunk Name"
              value={formik.values.name}
              onChange={formik.handleChange}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
            />
          </Grid>

          {/* IP/Domain */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              id="ip"
              name="ip"
              label="IP/Domain"
              value={formik.values.ip}
              onChange={formik.handleChange}
              error={formik.touched.ip && Boolean(formik.errors.ip)}
              helperText={formik.touched.ip && formik.errors.ip}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              id="port"
              name="port"
              label="Port"
              value={formik.values.port}
              onChange={formik.handleChange}
              error={formik.touched.port && Boolean(formik.errors.port)}
              helperText={formik.touched.port && formik.errors.port}
            />
          </Grid>

          {/* Prefix */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              id="prefix"
              name="prefix"
              label="Prefix"
              value={formik.values.prefix}
              onChange={formik.handleChange}
              error={formik.touched.prefix && Boolean(formik.errors.prefix)}
              helperText={formik.touched.prefix && formik.errors.prefix}
            />
          </Grid>

          {/* Remove Prefix */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              id="remove_prefix"
              name="remove_prefix"
              label="Remove Prefix"
              value={formik.values.remove_prefix}
              onChange={formik.handleChange}
              error={formik.touched.remove_prefix && Boolean(formik.errors.remove_prefix)}
              helperText={formik.touched.remove_prefix && formik.errors.remove_prefix}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              id="username"
              name="username"
              label="User Name"
              value={formik.values.username}
              onChange={formik.handleChange}
              error={formik.touched.user_name && Boolean(formik.errors.username)}
              helperText={formik.touched.user_name && formik.errors.username}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              id="password"
              name="password"
              label="Password"
              value={formik.values.password}
              onChange={formik.handleChange}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={formik.touched.password && formik.errors.password}
            />
          </Grid>

          {/* Fail Over */}
          <Grid item xs={12}>
            <FormControl
              fullWidth
              error={formik.touched.failover && Boolean(formik.errors.failover)}
            >
              <InputLabel id="failover-label">Fail Over</InputLabel>
              <Select
                labelId="failover-label"
                id="failover"
                name="failover"
                value={formik.values.failover}
                onChange={formik.handleChange}
                label="Fail Over"
                sx={{ height: "56px" }}
              >
                {failoverOptions.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.name}
                  </MenuItem>
                ))}
              </Select>

              <FormHelperText>{formik.touched.failover && formik.errors.failover}</FormHelperText>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              id="priority"
              name="priority"
              label="Priority"
              value={formik.values.priority}
              onChange={formik.handleChange}
              error={formik.touched.priority && Boolean(formik.errors.priority)}
              helperText={formik.touched.priority && formik.errors.priority}
            />
          </Grid>

          {/* Submit */}
          <Grid item xs={12}>
            <Button
              variant="contained"
              type="submit"
              fullWidth
              sx={{
                backgroundColor: "#1A73E8",
                color: "#FFFFFF",
                fontWeight: "bold",
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#1669C1",
                },
              }}
            >
              Submit
            </Button>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
}

AddTrunkForm.propTypes = {
  onSuccess: PropTypes.func,
  initialData: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    ip: PropTypes.string,
    prefix: PropTypes.string,
    remove_prefix: PropTypes.string,
    failover: PropTypes.number,
  }),
};

export default AddTrunkForm;
